import React, { useState, useEffect } from 'react';
import { Button } from '../layout/buttons';
import { Modal, Row, Col } from 'react-bootstrap';
import { PageHeader } from '../layout';
import SimpleTable from '../layout/table/SimpleTable';
import { fetchUserStakeholders, createStakeholder, updateStakeholder, deleteStakeholder } from '../../utils/stakeholders';
import { fetchUserCompanies } from '../../utils/companies';
import { logger } from '../../utils/logger';
import styles from './Profile.module.css';

const StakeholderManagement = ({ company, userData }) => {
  const [stakeholders, setStakeholders] = useState([]);
  const [userCompanies, setUserCompanies] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingStakeholder, setEditingStakeholder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state for creating stakeholder
  const [stakeholderData, setStakeholderData] = useState({
    name: '',
    parentCompanyId: '',
    isInternal: true,
    isPublic: false,
    objective: '',
    metadata: {
      stakeholderType: '',
      department: '',
      region: ''
    }
  });

  useEffect(() => {
    loadUserCompanies();
    loadStakeholders();
  }, []);

  const loadUserCompanies = async () => {
    try {
      const result = await fetchUserCompanies({ pageSize: 50 });
      setUserCompanies(result.companies || []);
      
      // Auto-select first company if available and no company is selected
      if (result.companies && result.companies.length > 0 && !stakeholderData.parentCompanyId) {
        setStakeholderData(prev => ({
          ...prev,
          parentCompanyId: result.companies[0].id
        }));
      }
    } catch (err) {
      logger.error('Error loading user organizations:', err);
    }
  };

  const loadStakeholders = async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = await fetchUserStakeholders({ 
        pageSize: 50
      });

      setStakeholders(result.stakeholders || []);
    } catch (err) {
      logger.error('Error loading user stakeholders:', err);
      setError('Failed to load stakeholders');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStakeholder = (stakeholder) => {
    setEditingStakeholder(stakeholder);
    setStakeholderData({
      name: stakeholder.name,
      parentCompanyId: stakeholder.parentCompanyId,
      isInternal: stakeholder.isInternal,
      isPublic: stakeholder.isPublic,
      objective: stakeholder.objective || '',
      metadata: {
        stakeholderType: stakeholder.metadata?.stakeholderType || '',
        department: stakeholder.metadata?.department || '',
        region: stakeholder.metadata?.region || ''
      }
    });
    setShowCreateModal(true);
  };

  const handleCreateOrUpdateStakeholder = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      // Validate required fields
      if (!stakeholderData.name.trim()) {
        setError('Stakeholder name is required');
        return;
      }

      // Validate that a company is selected
      if (!stakeholderData.parentCompanyId) {
        setError('Please select an organization for the stakeholder');
        return;
      }

      let result;
      if (editingStakeholder) {
        // Update existing stakeholder
        result = await updateStakeholder(editingStakeholder.id, stakeholderData);
      } else {
        // Create new stakeholder
        result = await createStakeholder({
          ...stakeholderData,
          parentCompanyId: stakeholderData.parentCompanyId
        });
      }
      
      if (result.success) {
        setShowCreateModal(false);
        setEditingStakeholder(null);
        loadStakeholders(); // Refresh the list
        
        // Reset form
        const firstCompanyId = userCompanies.length > 0 ? userCompanies[0].id : '';
        setStakeholderData({
          name: '',
          parentCompanyId: firstCompanyId,
          isInternal: true,
          isPublic: false,
          objective: '',
          metadata: {
            stakeholderType: '',
            department: '',
            region: ''
          }
        });
      } else {
        setError(result.message || `Failed to ${editingStakeholder ? 'update' : 'create'} stakeholder`);
      }
    } catch (err) {
      logger.error(`Error ${editingStakeholder ? 'updating' : 'creating'} stakeholder:`, err);
      setError(`Failed to ${editingStakeholder ? 'update' : 'create'} stakeholder`);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingStakeholder(null);
    // Reset form
    const firstCompanyId = userCompanies.length > 0 ? userCompanies[0].id : '';
    setStakeholderData({
      name: '',
      parentCompanyId: firstCompanyId,
      isInternal: true,
      isPublic: false,
      objective: '',
      metadata: {
        stakeholderType: '',
        department: '',
        region: ''
      }
    });
  };

  const handleDeleteStakeholder = async (stakeholderId) => {
    if (!window.confirm('Are you sure you want to delete this stakeholder?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const result = await deleteStakeholder(stakeholderId);
      
      if (result.success) {
        loadStakeholders(); // Refresh the list
      } else {
        setError(result.message || 'Failed to delete stakeholder');
      }
    } catch (err) {
      logger.error('Error deleting stakeholder:', err);
      setError('Failed to delete stakeholder');
    } finally {
      setLoading(false);
    }
  };

  const getStakeholderTypeLabel = (type) => {
    switch (type) {
      case 'employee': return 'Employee Group';
      case 'customer': return 'Customer Group';
      case 'partner': return 'Partner';
      case 'supplier': return 'Supplier';
      case 'investor': return 'Investor';
      case 'department': return 'Department';
      case 'team': return 'Team';
      default: return type || 'Stakeholder';
    }
  };

  if (userCompanies.length === 0) {
    return (
      <div className={styles.dataContainerCard}>
        <PageHeader 
          title="Stakeholder Management" 
          subtext="Please set up an organization first to manage stakeholders"
        />
      </div>
    );
  }

  return (
    <div className={styles.stakeholderManagement}>
      <div className={styles.dataContainerCard}>
        <PageHeader 
            title="Your Stakeholders" 
            subtext="Manage stakeholders across all your organizations"
          />
        
        {error && <div className={styles.errorMessage}>{error}</div>}
        
        {loading && stakeholders.length === 0 ? (
          <div>Loading stakeholders...</div>
        ) : (
          <SimpleTable
            headers={['Stakeholder Name', 'Type', 'Category', 'Organization', 'Department', 'Actions']}
            className={styles.stakeholdersTable}
          >
            {stakeholders.length === 0 ? (
              <tr>
                <td colSpan="6" className={styles.emptyStateCell}>
                  <div className={styles.emptyState}>
                    <p>No stakeholders found. Create your first stakeholder group to get started.</p>
                  </div>
                </td>
              </tr>
            ) : (
              stakeholders.map((stakeholder) => {
                const company = userCompanies.find(c => c.id === stakeholder.parentCompanyId);
                return (
                  <tr key={stakeholder.id}>
                    <td>
                      <div className={styles.stakeholderNameCell}>
                        <strong>{stakeholder.name}</strong>
                        {stakeholder.objective && (
                          <div className={styles.stakeholderObjective}>
                            {stakeholder.objective}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`${styles.typeBadge}`}>
                        {getStakeholderTypeLabel(stakeholder.metadata?.stakeholderType)}
                      </span>
                    </td>
                    <td>
                      <div className={styles.categoryCell}>
                        <span className={`${styles.categoryBadge} ${stakeholder.isInternal ? styles.internalCategory : styles.externalCategory}`}>
                          {stakeholder.isInternal ? 'Internal' : 'External'}
                        </span>
                        {stakeholder.isPublic && (
                          <span className={`${styles.visibilityBadge} ${styles.publicVisibility}`}>
                            Public
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={styles.companyName}>
                        {company?.name || 'Unknown Organization'}
                      </span>
                    </td>
                    <td>
                      <span className={styles.departmentName}>
                        {stakeholder.metadata?.department || 'Not specified'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <Button 
                          variant="outline-neutral" 
                          size="sm"
                          onClick={() => handleEditStakeholder(stakeholder)}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDeleteStakeholder(stakeholder.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </SimpleTable>
        )}

        <div className={styles.companyActions}>
          <Button 
            variant="primary" 
            onClick={() => setShowCreateModal(true)}
            className={styles.actionButton}
          >
            Create Stakeholder
          </Button>
        </div>
      </div>

      {/* Create/Edit Stakeholder Modal */}
      <Modal show={showCreateModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingStakeholder ? 'Edit Stakeholder' : 'Create Stakeholder'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <div className={styles.errorMessage}>{error}</div>}
          
          <form onSubmit={handleCreateOrUpdateStakeholder}>
            <Row>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Stakeholder Name *</label>
                  <input
                    type="text"
                    value={stakeholderData.name}
                    onChange={(e) => setStakeholderData(prev => ({ ...prev, name: e.target.value }))}
                    className={styles.formInput}
                    required
                    placeholder="e.g., Customer Support Team, Engineering Department"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Stakeholder Type</label>
                  <select
                    value={stakeholderData.metadata.stakeholderType}
                    onChange={(e) => setStakeholderData(prev => ({ 
                      ...prev, 
                      metadata: { ...prev.metadata, stakeholderType: e.target.value }
                    }))}
                    className={styles.formInput}
                  >
                    <option value="">Select type...</option>
                    <option value="department">Department</option>
                    <option value="team">Team</option>
                    <option value="employee">Employee Group</option>
                    <option value="customer">Customer Group</option>
                    <option value="partner">Partner</option>
                    <option value="supplier">Supplier</option>
                    <option value="investor">Investor</option>
                  </select>
                </div>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Organization *</label>
                  <select
                    value={stakeholderData.parentCompanyId}
                    onChange={(e) => setStakeholderData(prev => ({ ...prev, parentCompanyId: e.target.value }))}
                    className={styles.formInput}
                    required
                  >
                    {userCompanies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Department</label>
                  <input
                    type="text"
                    value={stakeholderData.metadata.department}
                    onChange={(e) => setStakeholderData(prev => ({ 
                      ...prev, 
                      metadata: { ...prev.metadata, department: e.target.value }
                    }))}
                    className={styles.formInput}
                    placeholder="e.g., Engineering, Marketing, Sales"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Region</label>
                  <input
                    type="text"
                    value={stakeholderData.metadata.region}
                    onChange={(e) => setStakeholderData(prev => ({ 
                      ...prev, 
                      metadata: { ...prev.metadata, region: e.target.value }
                    }))}
                    className={styles.formInput}
                    placeholder="e.g., North America, Europe, APAC"
                  />
                </div>
              </Col>
            </Row>

            <div className={styles.formGroup}>
              <label>Objective</label>
              <textarea
                value={stakeholderData.objective}
                onChange={(e) => setStakeholderData(prev => ({ ...prev, objective: e.target.value }))}
                className={styles.formTextarea}
                placeholder="What is this stakeholder group's main objective or purpose?"
                rows={3}
              />
            </div>

            <Row>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={stakeholderData.isInternal}
                      onChange={(e) => setStakeholderData(prev => ({ ...prev, isInternal: e.target.checked }))}
                      className={styles.checkbox}
                    />
                    Internal Stakeholder
                  </label>
                  <small className={styles.helpText}>
                    Internal stakeholders are part of your organization (employees, departments)
                  </small>
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={stakeholderData.isPublic}
                      onChange={(e) => setStakeholderData(prev => ({ ...prev, isPublic: e.target.checked }))}
                      className={styles.checkbox}
                    />
                    Publicly Discoverable
                  </label>
                  <small className={styles.helpText}>
                    Public stakeholders can be discovered by others in your organization
                  </small>
                </div>
              </Col>
            </Row>

            <div className={styles.modalActions}>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (editingStakeholder ? 'Updating...' : 'Creating...') : (editingStakeholder ? 'Update Stakeholder' : 'Create Stakeholder')}
              </Button>
              <Button 
                variant="outline-neutral" 
                type="button"
                onClick={handleCloseModal}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default StakeholderManagement;
